import { memo, useMemo } from "react";
import { Home, Heart, Menu, Plus, Wallet } from "lucide-react";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

interface BottomNavProps {
  activeTab: string;
  onTabChange?: (tab: string) => void;
  favoritesCount?: number;
  onMenuClick?: () => void;
  onPostAdClick?: () => void;
  /** Visual hint: onboarding done but e-mail not verified (parent still runs onPostAdClick → toast). */
  postAdBlockedByUnverifiedEmail?: boolean;
  /** When false, hide Início, Anunciar, Favoritos, Conta Digital, and Menu (bottom nav shows empty/spacers). */
  isLoggedIn?: boolean;
}

export const BottomNav = memo(function BottomNav({ 
  activeTab, 
  onTabChange = () => {}, 
  favoritesCount = 0,
  onMenuClick = () => {},
  onPostAdClick = () => {},
  postAdBlockedByUnverifiedEmail = false,
  isLoggedIn: _isLoggedInProp = true,
}: BottomNavProps) {
  const accountNav = useAccountNavEnabled();

  const tabs = useMemo(() => {
    const base: { id: string; label: string; icon: typeof Home; count?: number }[] = [];
    if (accountNav) {
      base.push({ id: "home", label: "Início", icon: Home });
      base.push({ id: "favorites", label: "Favoritos", icon: Heart, count: favoritesCount });
    }
    return base;
  }, [favoritesCount, accountNav]);

  const labelClass = "text-xs mt-1";

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 pb-safe">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between h-16 min-h-16">
          {/* Left tabs (Início + Favoritos when logged in); spacer when not logged in */}
          {!accountNav && <div className="flex-1 min-w-0" aria-hidden />}
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex flex-1 min-w-0 flex-col items-center justify-end pb-2 pt-1 h-full transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
                aria-label={tab.label}
                aria-current={isActive ? "page" : undefined}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="w-6 h-6" />
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {tab.count > 9 ? "9+" : tab.count}
                    </span>
                  )}
                </div>
                <span className={labelClass}>{tab.label}</span>
              </button>
            );
          })}

          {/* Center Post Ad Button (only when logged in) */}
          {accountNav && (
            <button
              type="button"
              onClick={onPostAdClick}
              title={
                postAdBlockedByUnverifiedEmail
                  ? "Confirme seu e-mail para criar anúncios"
                  : undefined
              }
              className="flex flex-1 min-w-0 flex-col items-center justify-end pb-2 pt-1 h-full transition-colors"
              aria-label="Anunciar"
              aria-disabled={postAdBlockedByUnverifiedEmail || undefined}
            >
              <div
                className={`w-14 h-14 flex-shrink-0 mb-0.5 bg-primary rounded-full flex items-center justify-center shadow-lg transition-colors ${
                  postAdBlockedByUnverifiedEmail
                    ? "opacity-50 saturate-50"
                    : "hover:bg-primary/90"
                }`}
              >
                <Plus className="w-7 h-7 text-primary-foreground" />
              </div>
              <span
                className={`${labelClass} ${postAdBlockedByUnverifiedEmail ? "text-muted-foreground" : "text-foreground"}`}
              >
                Anunciar
              </span>
            </button>
          )}

          {/* Right tabs - Conta Digital and Menu (only when logged in). Spacer when not logged in. */}
          {accountNav ? (
            <>
              <button
                onClick={() => onTabChange("wallet")}
                className={`flex flex-1 min-w-0 flex-col items-center justify-end pb-2 pt-1 h-full transition-colors ${
                  activeTab === "wallet" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
                aria-label="Conta Digital"
                aria-current={activeTab === "wallet" ? "page" : undefined}
              >
                <Wallet className="w-6 h-6 flex-shrink-0" />
                <span className={labelClass}>Conta Digital</span>
              </button>

              <button
                onClick={onMenuClick}
                className="flex flex-1 min-w-0 flex-col items-center justify-end pb-2 pt-1 h-full text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Menu"
              >
                <Menu className="w-6 h-6 flex-shrink-0" />
                <span className={labelClass}>Menu</span>
              </button>
            </>
          ) : (
            <div className="flex-1 min-w-0" aria-hidden />
          )}
        </div>
      </div>
    </nav>
  );
});